import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { Icon } from '@/components/ui/Icon/Icon';
import { nb } from '@/i18n/nb';
import s from './ImageViewerOverlay.module.css';

type ImageViewerOverlayProps = {
  open: boolean;
  onClose: () => void;
  /** Signed viewer-variant URL (1200²) from GalleryImageResource. */
  src: string;
  /** Alt + caption — the entry description is the natural choice in Vedlikehold. */
  alt: string;
};

/*
 * Minimal full-bleed image viewer for the Vedlikehold thumb-tap UX. Built
 * purpose-built (not via <Modal>) because the modal chrome — title bar, padding —
 * doesn't fit a fullscreen image viewer. Same primitives though: portal +
 * focus trap + Esc-from-anywhere + backdrop-click-closes.
 *
 * Iter 8 Galleri will need a richer viewer (caption display, prev/next, delete
 * affordance for own images, swipe-to-dismiss on touch); at that point this can
 * be promoted to components/ui/ImageViewer/ and extended, or replaced wholesale.
 */
export function ImageViewerOverlay({ open, onClose, src, alt }: ImageViewerOverlayProps) {
  const trapRef = useFocusTrap<HTMLDivElement>(open);

  // Esc closes from anywhere while open — same idiom as <Modal>.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={s.backdrop}
      onPointerDown={(e) => {
        // Backdrop click closes; clicks inside the image / close button don't bubble here.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={trapRef}
        className={s.viewer}
        role="dialog"
        aria-modal="true"
        aria-label={nb.vedlikehold.viewer.label}
      >
        <button
          type="button"
          className={s.close}
          onClick={onClose}
          aria-label={nb.vedlikehold.viewer.closeAria}
        >
          <Icon name="x" size={24} />
        </button>
        {/* The viewer variant maxes out at 1200² (ImagePipelineService); object-fit
            contain centers it inside the available viewport so portrait + landscape
            both render edge-padded rather than cropped. */}
        <img className={s.image} src={src} alt={alt} />
      </div>
    </div>,
    document.body,
  );
}
